/**
 * The butler's client (WP 9.2): history read straight from Supabase, the
 * turn in flight read off the Worker's SSE stream, and the event rows a
 * `cards` part names.
 *
 * **History is the database's, not the Worker's.** `useThreads` and
 * `useThread` read `chat_threads`/`chat_messages` through the app's own
 * session and RLS (`20260928120000_butler.sql`), so a cold start paints the
 * day's thread from the persisted query cache before anything is asked. The
 * Worker only ever streams the one turn being asked.
 *
 * **The turn in flight is local until its row exists.** `useAsk` folds each
 * event into `live` (`chatClient.ts`'s `reduceTurn`, which a test can see);
 * `done` invalidates `['chat', threadId]`, and `live` stops being drawn only
 * once the refetched rows actually contain `done`'s `message_id`. Clearing
 * it on `done` itself would blank the answer for the length of a refetch —
 * and forever against the stub, which persists nothing.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { supabase } from './supabase'
import { useAuth } from './auth'
import { queryClient } from './store'
import { useAuthed, useFeed, useScores } from './queries'
import { SseDecoder, type ChatRequest, type MessagePart } from './chatProtocol'
import {
  EMPTY_TURN, endTurn, exchangeToWithdraw, failureFor, firstQuestions, reduceTurn, stubParams, type TrailStep, type TurnState,
} from './chatClient'
import type { FeedRow } from './types'
import { dropTwins } from './twins'
import { withScores } from './userScores'
import { getActiveRegionId } from './regions'

export { applyEvent } from './chatClient'

const WORKER_URL = (import.meta.env.VITE_WORKER_URL as string | undefined)?.replace(/\/+$/, '')

export interface ChatThread {
  id: string
  title: string | null
  created_at: string
  last_at: string
  /** a2-02 — how many messages it holds; 0 is a thread nothing was said in. */
  messages: number
}

export interface ChatMessage {
  id: string
  thread_id: string
  role: 'user' | 'assistant'
  parts: MessagePart[]
  created_at: string
}

export type Outcome = ChatRequest['outcomes'][number]

/** What the app sends to `/chat`: the protocol's `ChatRequest` and the region
 *  being looked at (C2.2, D2: "Ask is told the region"). The Worker reads it
 *  as an optional field and falls back to the person's current region, so a
 *  bundle that sends it ahead of the Worker that reads it costs nothing —
 *  `parseChatRequest` copies only the fields it knows. */
type AskBody = ChatRequest & { region: string }

// ---------------------------------------------------------------------------
// History
// ---------------------------------------------------------------------------

async function select<T>(
  q: PromiseLike<{ data: T[] | null; error: { message: string } | null }>,
): Promise<T[]> {
  const { data, error } = await q
  if (error) throw new Error(error.message)
  return data ?? []
}

/** Every thread, most recently used first. */
export function useThreads(): UseQueryResult<ChatThread[]> {
  return useAuthed({
    queryKey: ['chat_threads'],
    queryFn: async () => {
      const rows = await select<Omit<ChatThread, 'messages'> & { chat_messages: { count: number }[] | null }>(
        supabase.from('chat_threads').select('id,title,created_at,last_at,chat_messages(count)').order('last_at', { ascending: false }),
      )
      return rows.map(({ chat_messages, ...t }) => ({ ...t, messages: chat_messages?.[0]?.count ?? 0 }))
    },
  })
}

/** a3-26: each listed thread's first question, for the desk's thread list.
 *  Read only while the list is open (`ids` empty answers without a request,
 *  as `useThread` does, because `useAuthed` owns `enabled`). */
export function useFirstQuestions(ids: readonly string[]): UseQueryResult<Map<string, string>> {
  return useAuthed({
    queryKey: ['chat_first', [...ids].sort().join(',')],
    queryFn: async () => ids.length
      ? firstQuestions(await select<{ thread_id: string; created_at: string; parts: MessagePart[] | null }>(
          supabase.from('chat_messages').select('thread_id,created_at,parts')
            .eq('role', 'user').in('thread_id', [...ids]).order('created_at', { ascending: true }),
        ))
      : new Map<string, string>(),
  })
}

/** One thread's messages, oldest first. `null` is the thread that does not
 *  exist yet — an empty list, answered without a request (`useAuthed` owns
 *  `enabled`, so the id is checked inside the query instead). */
export function useThread(id: string | null): UseQueryResult<ChatMessage[]> {
  return useAuthed({
    queryKey: ['chat', id],
    queryFn: () => id
      ? select<ChatMessage>(
          supabase.from('chat_messages').select('id,thread_id,role,parts,created_at')
            .eq('thread_id', id).order('created_at', { ascending: true }),
        )
      : Promise.resolve([]),
  })
}

// ---------------------------------------------------------------------------
// The turn in flight
// ---------------------------------------------------------------------------

export interface Ask {
  /** `thread`: the thread to ask in, when the screen made one for this
   *  question (a2-02) and the hook's own `threadId` is still `null`. */
  ask: (text: string, outcomes: Outcome[], thread?: string) => Promise<void>
  stop: () => void
  /** WP 9.12 — forget the turn drawn from local state (Edit on a turn that
   *  has not been refetched yet). Nothing while one is streaming. */
  clear: () => void
  streaming: boolean
  /** The turn in flight, rebuilt from events as they arrive. */
  live: MessagePart[]
  /** The latest `status` label, null once text starts. */
  status: string | null
  /** WP 9.12 — the thinking summary streamed so far; '' once settled. */
  thinking: string
  /** WP 9.12 — what the turn has done so far, one step per status. */
  trail: TrailStep[]
  /** WP 9.12 — `done`'s message id while the turn is still drawn from local
   *  state, so the screen can keep its thinking beside the row it became. */
  doneId: string | null
  error: { message: string; retryable: boolean } | null
  /** From the `x-thread-id` response header. */
  threadId: string | null
  /** What Doug asked, drawn the moment `ask` is called (the optimistic user
   *  message) and until the persisted rows replace the turn. */
  asked: string | null
  /** True when the turn was cut short (the stream closed without `done` or
   *  an error of its own), whatever `error.message` then says: "Interrupted",
   *  or "No connection" when the phone was offline (a2-07, `failureFor`).
   *  Never string-match the message to learn which. */
  interrupted: boolean
}

/** a2-07 — an error status before any stream, in words (`failureFor`). */
async function httpFailure(res: Response): Promise<{ message: string; retryable: boolean }> {
  let detail = ''
  try { detail = (await res.text()).trim().slice(0, 200) } catch { /* the status alone will do */ }
  return failureFor({ kind: 'http', status: res.status, detail })
}

/** False only when the browser is sure it is offline; `navigator.onLine`
 *  true says little (a captive portal is "online"), false is reliable. */
const online = () => typeof navigator === 'undefined' || navigator.onLine !== false

export function useAsk(threadId: string | null): Ask {
  const [turn, setTurn] = useState<TurnState>(EMPTY_TURN)
  const [asked, setAsked] = useState<string | null>(null)
  const [streaming, setStreaming] = useState(false)
  const [headerThread, setHeaderThread] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  // A screen that unmounts mid-turn (a new thread, a thread picked from the
  // sheet) takes its stream with it rather than leaving a reader running
  // into state nobody draws.
  useEffect(() => () => abortRef.current?.abort(), [])

  const ask = useCallback(async (text: string, outcomes: Outcome[], made?: string) => {
    if (abortRef.current) return // one turn at a time; the composer is disabled anyway
    const ctl = new AbortController()
    abortRef.current = ctl

    let s: TurnState = EMPTY_TURN
    const push = (next: TurnState) => { s = next; setTurn(next) }
    setAsked(text)
    setTurn(EMPTY_TURN)
    setStreaming(true)

    let responded = false
    const target = made ?? threadId
    let thread = target
    // A thread made for this question is this turn's from the start, as the
    // header would make it: the screen adopts it at once, so a failure
    // before any response still leaves Doug in the thread he asked in.
    if (made) setHeaderThread(made)
    try {
      if (!WORKER_URL) throw new Error('VITE_WORKER_URL is not set')
      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token
      if (!token) throw new Error('Not signed in')

      let url = `${WORKER_URL}/chat`
      // Dev only: `?stub=pause_after=2` on the page reaches the stub's own
      // query. `import.meta.env.DEV` is a literal `false` in a production
      // build, so this block — and `stubParams` with it — is dropped.
      if (import.meta.env.DEV) {
        const q = stubParams(window.location.search)
        if (q) url += `?${q}`
      }

      const body: AskBody = { thread_id: target, text, outcomes, region: getActiveRegionId() }
      const res = await fetch(url, {
        method: 'POST',
        headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
        body: JSON.stringify(body),
        signal: ctl.signal,
      })
      responded = true
      if (!res.ok || !res.body) {
        push({ ...s, error: await httpFailure(res) })
        return
      }

      thread = res.headers.get('x-thread-id') ?? target
      if (thread) setHeaderThread(thread)

      const reader = res.body.pipeThrough(new TextDecoderStream()).getReader()
      const decoder = new SseDecoder()
      for (;;) {
        const { value, done } = await reader.read()
        if (done) break
        for (const e of decoder.push(value)) push(reduceTurn(s, e))
      }
      for (const e of decoder.flush()) push(reduceTurn(s, e))
      push(endTurn(s, online()))
    } catch (err) {
      // Before a response: nothing reached the Worker, which is an error to
      // show. After one (a dropped connection, or Stop): the turn was cut
      // short, which is `endTurn`'s interrupted state unless the stream had
      // already said `done` or `error` itself.
      //
      // a2-07: before a response, `fetch` throwing a TypeError is the network
      // (Chrome "Failed to fetch", Safari "Load failed") — "No connection",
      // as is a phone that knows it is offline. Anything else thrown before
      // the request (not signed in, no Worker URL) keeps its own words.
      if (responded || ctl.signal.aborted) push(endTurn(s, online()))
      else if (!online() || err instanceof TypeError) push({ ...s, error: failureFor({ kind: 'offline' }) })
      else push({ ...s, error: { message: err instanceof Error ? err.message : 'Could not reach Ask.', retryable: true } })
    } finally {
      abortRef.current = null
      setStreaming(false)
    }

    if (s.doneId && thread) {
      // `refetchType: 'all'`: the screen may not be observing this key yet —
      // it adopts `threadId` from the header a render after the first byte.
      void queryClient.invalidateQueries({ queryKey: ['chat', thread], refetchType: 'all' })
      void queryClient.invalidateQueries({ queryKey: ['chat_threads'], refetchType: 'all' })
    }
  }, [threadId])

  const stop = useCallback(() => { abortRef.current?.abort() }, [])
  const clear = useCallback(() => {
    if (abortRef.current) return
    setTurn(EMPTY_TURN)
    setAsked(null)
  }, [])

  // Settled: the persisted rows now carry this turn, so they draw it and the
  // local copy steps aside. Same query key as the screen's own `useThread`,
  // so this is a read of the cache, not a second request.
  const persisted = useThread(headerThread ?? threadId).data
  const settled = !!turn.doneId && !!persisted?.some(m => m.id === turn.doneId)
  // Echoed: the Worker persists the question before it answers, and a new
  // thread's query runs the moment its id arrives in the header — so for
  // the rest of the turn the persisted row and the optimistic bubble both
  // drew (Doug, 29 Sept: "shows the message I sent twice before it
  // condenses to one"). Once the last persisted row is that question, the
  // local copy has nothing left to say.
  const last = persisted?.[persisted.length - 1]
  const echoed = asked !== null && last?.role === 'user'
    && (last.parts ?? []).some(p => p.type === 'text' && p.text === asked)

  return {
    ask,
    stop,
    clear,
    streaming,
    live: settled ? [] : turn.parts,
    status: turn.status,
    thinking: settled ? '' : turn.thinking,
    trail: turn.trail,
    doneId: turn.doneId,
    error: turn.error,
    threadId: headerThread,
    asked: settled || echoed ? null : asked,
    interrupted: turn.interrupted,
  }
}

// ---------------------------------------------------------------------------
// Edit and Retry (WP 9.12)
// ---------------------------------------------------------------------------

/**
 * Takes Doug's last message and its answer out of the thread, through his
 * own session — they are his rows, and RLS (`chat_messages_delete`,
 * `chat_messages_update`: `user_id = auth.uid()`) lets him. Edit calls this
 * and puts the words back in the composer; Retry calls it and asks the same
 * words again, so the thread reads question, new answer — not the question
 * twice.
 *
 * **The question is deleted; the answer is emptied, not deleted.**
 * `chat_messages` is also the spend ledger: `src/butler/spend.ts` sums
 * `cost_usd` over assistant rows for the day's cap and the week's line, so
 * deleting an answer would hand its cost back to the cap — an edit would be
 * a way round a hard budget (rule 5). An answer with no parts keeps its
 * cost and usage and draws nothing (`Thread` skips it). What the model
 * reads back for it is `history.ts`'s own line for an empty row, "[the turn
 * was interrupted]", folded into the answer before it — which is true
 * enough, and a one-line skip there is filed rather than made here.
 *
 * The ids come from a fresh read, not the screen's cache: a turn whose
 * `done` has not been refetched is drawn from local state and its rows'
 * ids are not known to it (`exchangeToWithdraw` says why the text is the
 * check). Resolves to the ids taken out — none when nothing of this
 * exchange had been persisted yet.
 */
export async function withdrawExchange(threadId: string, text: string): Promise<string[]> {
  const recent = await select<ChatMessage>(
    supabase.from('chat_messages').select('id,thread_id,role,parts,created_at')
      .eq('thread_id', threadId).order('created_at', { ascending: false }).limit(6),
  )
  const pick = exchangeToWithdraw(recent.reverse(), text)
  if (!pick) return []
  const del = await supabase.from('chat_messages').delete().eq('id', pick.question)
  if (del.error) throw new Error(del.error.message)
  if (pick.answer) {
    const blank = await supabase.from('chat_messages').update({ parts: [] }).eq('id', pick.answer)
    if (blank.error) throw new Error(blank.error.message)
  }
  void queryClient.invalidateQueries({ queryKey: ['chat', threadId] })
  return pick.answer ? [pick.question, pick.answer] : [pick.question]
}

// ---------------------------------------------------------------------------
// The spend_more chip's retry (WP 9.9)
// ---------------------------------------------------------------------------

/**
 * Re-asks the question a `spend_more` chip carries, once its grant row is
 * inserted. A bare request, not a hook: `Proposal.tsx` fires this on a tap
 * without needing the thread's own `useAsk` instance threaded down through
 * `Thread.tsx`, which this package does not own and did not need to touch.
 *
 * `thread_id: null` — "today's thread" on the wire — lands on the same
 * thread this chip's own turn just touched (`persist.ts`'s `openThread`:
 * the most recent one active since Berlin midnight), so the retry does not
 * need to know an id it was never given.
 *
 * It does not stream into any local state. The answer arrives the same way
 * any other persisted turn's does: through `useThread`'s `['chat', id]`
 * query, invalidated here once the response ends — the same pair of
 * `invalidateQueries` calls `useAsk`'s own `ask` makes at `done`. Two real
 * requests make two real rows: the question the day's cap answered from the
 * database, and the same question asked again with the grant in place. Each
 * shows once, for the turn that made it — not a rendering bug, and not
 * `useAsk`'s optimistic `asked`/`live` state either, since this never
 * touches it.
 */
export async function retryAsk(text: string, outcomes: Outcome[]): Promise<void> {
  if (!WORKER_URL) throw new Error('VITE_WORKER_URL is not set')
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  if (!token) throw new Error('Not signed in')

  const body: AskBody = { thread_id: null, text, outcomes, region: getActiveRegionId() }
  const res = await fetch(`${WORKER_URL}/chat`, {
    method: 'POST',
    headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok || !res.body) throw new Error((await httpFailure(res)).message)

  const thread = res.headers.get('x-thread-id')
  // Nothing reads the stream live — drain it so the request completes and
  // the connection closes cleanly, the same as `useAsk`'s reader loop
  // without the per-chunk decode this caller has no use for.
  const reader = res.body.getReader()
  for (;;) {
    const { done } = await reader.read()
    if (done) break
  }

  if (thread) {
    void queryClient.invalidateQueries({ queryKey: ['chat', thread], refetchType: 'all' })
    void queryClient.invalidateQueries({ queryKey: ['chat_threads'], refetchType: 'all' })
  }
}

// ---------------------------------------------------------------------------
// Spend (WP 9.5)
// ---------------------------------------------------------------------------

/** `queries.ts`'s own `weekAgo()`, restated — that name is not exported, and
 *  a rolling seven days is the account sheet's existing "this week" (the
 *  repair-spend figure this line sits under reads the same way, through
 *  `useHarvestRuns()`). The Worker's own `/health` figure uses a Berlin
 *  Monday-to-now week instead (`src/butler/spend.ts`) — a different "this
 *  week" for a different reader, not a value that has to agree with this
 *  one to the cent. */
const weekAgo = () => new Date(Date.now() - 7 * 86400000).toISOString()

/**
 * This week's butler spend, in dollars — summed client-side through the
 * app's own session and RLS (`chat_messages_read`), the same split
 * `useThread` makes for history: the Worker is not involved in reading it
 * back. `0` while pending or signed out, same as a fresh account's "nothing
 * yet" — the account sheet's line only distinguishes zero from positive.
 */
export function useChatSpend(): number {
  const q = useAuthed({
    queryKey: ['chat_spend_week'],
    queryFn: () => select<{ cost_usd: number | null }>(
      supabase.from('chat_messages').select('cost_usd').eq('role', 'assistant').gte('created_at', weekAgo()),
    ),
  })
  return (q.data ?? []).reduce((sum, r) => sum + Number(r.cost_usd ?? 0), 0)
}

// ---------------------------------------------------------------------------
// The rows a `cards` part names
// ---------------------------------------------------------------------------

/** A `Map` from entry id to row, as the brief fixes it, that also says
 *  whether a lookup for the rest is still out — so a card not found yet
 *  draws as loading and only a card not found at all says "No longer
 *  listed". */
export interface CardsById extends Map<string, FeedRow> {
  readonly pending: boolean
}

/**
 * The rows for `ids`: from `useFeed()`'s cache where the row is there, and
 * the rest from `feed_lite` by id in one request. Outside the app's
 * twelve-week horizon is the normal case — "what is on in December" — not
 * an error. `feed_detail` returns score reasons, not a row, so it is not
 * the fallback.
 *
 * The fallback waits for the feed to settle, so a cold start does not ask
 * `feed_lite` for rows the feed is about to hand over anyway.
 */
export function useCardsById(ids: string[]): CardsById {
  const { session } = useAuth()
  const feedQ = useFeed()
  const feedRows = feedQ.data

  const byId = useMemo(() => {
    const m = new Map<string, FeedRow>()
    for (const r of feedRows ?? []) m.set(r.id, r)
    return m
  }, [feedRows])

  const wanted = useMemo(() => [...new Set(ids)].sort(), [ids.join(',')]) // eslint-disable-line react-hooks/exhaustive-deps
  const missing = useMemo(() => wanted.filter(id => !byId.has(id)), [wanted, byId])

  const extraQ = useQuery<FeedRow[]>({
    queryKey: ['feed_lite_by_id', missing],
    queryFn: () => select<FeedRow>(supabase.from('feed_lite').select('*').in('id', missing)),
    enabled: !!session && !feedQ.isPending && missing.length > 0,
    staleTime: 10 * 60_000,
  })

  // `feed_lite` does not filter twins (see `twins.ts`), and a twin's id is
  // exactly what lands in `missing` — `deriveFeed` dropped it from the cache.
  // WP U2.3: a row from outside the horizon carries no score of its own either
  // (`feed_lite` no longer has the column); lay the person's on it as `useFeed`
  // does, so a card's `score` means one thing wherever the row came from.
  const scores = useScores().data
  const extra = useMemo(
    () => extraQ.data && withScores(dropTwins(extraQ.data), scores),
    [extraQ.data, scores],
  )
  const pending = feedQ.isPending || (missing.length > 0 && extraQ.isPending && extraQ.fetchStatus !== 'idle')
  return useMemo(() => {
    const m = new Map<string, FeedRow>()
    for (const id of wanted) {
      const row = byId.get(id) ?? extra?.find(r => r.id === id)
      if (row) m.set(id, row)
    }
    return Object.assign(m, { pending })
  }, [wanted, byId, extra, pending])
}
